import { useState } from 'react';
import { Play, AlertTriangle, CheckCircle2, RefreshCw, Search, Lock, Unlock, Download, Plus } from 'lucide-react';
import client from '../api/client';
import { useAuth, userIsAdmin, userIsAccountant } from '../context/AuthContext';
import { NAVY, BORDER_COLOR, bubbleStyle, navyBtn, fieldStyle } from '../styles/tokens';
import { descargarCSV } from '../utils/csv';

/**
 * V57 — PTO mensual: el reporte de la contable Y el cierre que lo produce, en UNA pantalla.
 *
 * POR QUÉ JUNTOS: para la contable son el mismo momento de trabajo — revisa las horas,
 * cierra el mes, y quiere ver la tabla que salió. Separarlos la obligaba a cambiar de
 * sección para completar una sola tarea. El aviso de "no está sellado" vive al lado de la
 * tabla, que es donde tiene sentido: en una pantalla aparte nadie ve los números que no
 * están sellados.
 *
 * LA TABLA REPRODUCE EL EXCEL: UNA fila por persona, y las 7 métricas desplegadas
 * HORIZONTALMENTE dentro de cada mes (7 meses × 7 métricas = 49 columnas de datos), con
 * encabezado de dos niveles. La orientación no es cosmética: el contraste se hace fila
 * contra fila con el Excel al lado, y con los ejes invertidos 153 personas ocupan 1071
 * filas y comparar es imposible.
 *
 * CERRAR y SELLAR son distintos y acá solo se cierra. El período queda recalculable hasta
 * que se valide contra el Excel.
 */

const TAM_LOTE = 50;
const MESES = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

/** Las 7 métricas por mes, en el ORDEN DEL EXCEL. No reordenar sin mirar la hoja. */
const METRICAS = [
  { key: 'hrs_trab', label: 'hrs' },
  { key: 'vac_acum', label: 'vAc' },
  { key: 'vac_paid', label: 'vPd' },
  { key: 'bal_vac',  label: 'BALv', fuerte: true },
  { key: 'enf_acum', label: 'eAc' },
  { key: 'enf_paid', label: 'ePd' },
  { key: 'bal_enf',  label: 'BALe', fuerte: true },
];

const RAZONES = {
  unclassified:        'Sin clasificar (falta definir el régimen de PTO)',
  no_accrual:          'Fuera del régimen de acumulación (subcontratados)',
  persona_not_found:   'La persona no existe',
  month_closed:        'El mes está sellado',
  later_month_sealed:  'Hay un mes posterior sellado',
  later_months_closed: 'Hay meses posteriores ya calculados',
};

const n2 = v => (v === null || v === undefined ? null : Number(v).toFixed(2));

const W_ID = 62, W_NOM = 172, H_FILA1 = 26;

export default function PtoMonthlyPanel() {
  const { user } = useAuth();
  /**
   * UN SOLO FLAG PARA LAS TRES ACCIONES QUE ESCRIBEN — cerrar, ajustar y sellar.
   *
   * ⚠ No es que "quedó uno de antes": los tres permisos se ensancharon JUNTOS a la
   * contable en V64 y por la misma razón (que opere el cierre mensual sola), así que
   * hoy resuelven al mismo conjunto y escribirlos por separado sería tres expresiones
   * de una sola regla — el patrón que V44/V48/V49 vienen cerrando. Los que sí están
   * separados son los del backend (ROLES_CIERRE, ROLES_AJUSTE, ROLES_SELLO), que es
   * donde el permiso se decide de verdad.
   *
   * Si alguna vez uno diverge —p. ej. si reabrir pasa a ser solo de admin— ACÁ es donde
   * se parte, y la condición del backend tiene que moverse en el mismo commit: ocultar y
   * restringir son la MISMA decisión, y media decisión deja un botón que devuelve 403.
   */
  const puedeOperar = userIsAdmin(user) || userIsAccountant(user);
  const hoy = new Date();
  const [year, setYear] = useState(hoy.getFullYear());
  const [fromMonth, setFromMonth] = useState(1);
  const [untilMonth, setUntilMonth] = useState(7);

  const [data, setData] = useState(null);
  const [busca, setBusca] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  const [preview, setPreview] = useState(null);
  const [confirmando, setConfirmando] = useState(false);
  const [corriendo, setCorriendo] = useState(false);
  const [progreso, setProgreso] = useState(null);
  const [resultado, setResultado] = useState(null);

  // V64 — sellos y ajustes del período
  const [sellos, setSellos] = useState([]);
  const [ajustes, setAjustes] = useState([]);
  // V64 — el bloque de Análisis de Costo del export. Viene del MISMO endpoint que
  // alimenta el dashboard de saldos (V62): las tres reglas del costo se calculan en UN
  // solo lugar y acá solo se leen. Reimplementarlas para el export sería la segunda
  // expresión de la misma regla, que es el patrón que la serie viene cerrando.
  const [costos, setCostos] = useState(null);        // { porPersona: Map, totals } | null
  const [costosVeda, setCostosVeda] = useState(false); // sin permiso para verlos
  const [sello, setSello] = useState(null);          // preview del sello en revisión
  const [reabriendo, setReabriendo] = useState(null);// mes que se está por reabrir
  const [ajustando, setAjustando] = useState(null);  // persona sobre la que se ajusta

  const rangoOk = untilMonth >= fromMonth;
  const sellados = sellos.map(s => s.month);
  const mesesSellados = (() => {
    const out = [];
    for (let m = fromMonth; m <= untilMonth; m++) if (sellados.includes(m)) out.push(m);
    return out;
  })();
  const totalMeses = Math.max(0, untilMonth - fromMonth + 1);
  const estadoSello = mesesSellados.length === 0 ? 'ninguno'
                    : mesesSellados.length === totalMeses ? 'todo' : 'parcial';

  /**
   * Las tres lecturas del período van JUNTAS y detrás del mismo botón. Separarlas
   * dejaría la franja de sellos hablando de un año y la tabla de otro — el estado del
   * sello es lo que dice si los números de la tabla son definitivos, así que no puede
   * quedar desfasado de ella.
   */
  async function cargarReporte() {
    setCargando(true); setError(null);
    try {
      const [rep, sel, aj] = await Promise.all([
        client.get('/api/pto/reporte', { params: { year, fromMonth, untilMonth } }),
        client.get('/api/pto/sello',   { params: { year } }),
        client.get('/api/pto/ajustes', { params: { year, fromMonth, untilMonth } }),
      ]);
      setData(rep.data);
      setSellos(sel.data.sellados || []);
      setAjustes(aj.data.ajustes || []);

      // El costo va APARTE del Promise.all de arriba porque su permiso es distinto:
      // `/balances-report` alcanza a socio + contable + admin, y esta pantalla también la
      // ve el REVISOR. Metido en el Promise.all, su 403 tumbaría el reporte entero para
      // él. Se pide después y su fracaso solo apaga el bloque de costo.
      try {
        const { data: bal } = await client.get('/api/vacation/balances-report', { params: { year } });
        setCostos({
          porPersona: new Map((bal.rows || []).map(r => [r.persona_id, r])),
          totals: bal.totals,
        });
        setCostosVeda(false);
      } catch (e) {
        setCostos(null);
        setCostosVeda(e.response?.status === 403);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo generar el reporte.');
    } finally { setCargando(false); }
  }

  /** Después de sellar, reabrir o ajustar: todo se relee de una sola fuente. */
  const recargar = () => cargarReporte();

  async function cargarPreview() {
    setError(null); setResultado(null);
    try {
      const { data } = await client.get('/api/pto/cierre/preview', { params: { year, fromMonth } });
      setPreview(data);
      setConfirmando(true);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cargar el censo.');
    }
  }

  /** Lotes SECUENCIALES: cada persona escribe en su transacción y el orden de meses
   *  importa, así que serializar mantiene el progreso real y no una animación. */
  async function correr(ids) {
    setCorriendo(true); setError(null);
    const runId = crypto.randomUUID();
    const lotes = [];
    for (let i = 0; i < ids.length; i += TAM_LOTE) lotes.push(ids.slice(i, i + TAM_LOTE));
    const acc = { closed: 0, already: 0, refused: 0, failed: 0,
                  refusedByReason: {}, failures: [], runId, lotes: lotes.length };
    try {
      for (let i = 0; i < lotes.length; i++) {
        setProgreso({ lote: i + 1, lotes: lotes.length,
                      personas: Math.min((i + 1) * TAM_LOTE, ids.length), total: ids.length });
        const { data } = await client.post('/api/pto/cierre/lote',
          { year, fromMonth, untilMonth, personaIds: lotes[i], runId });
        acc.closed += data.closed; acc.already += data.already;
        acc.refused += data.refused; acc.failed += data.failed;
        for (const [k, v] of Object.entries(data.refusedByReason || {}))
          acc.refusedByReason[k] = (acc.refusedByReason[k] || 0) + v;
        acc.failures.push(...(data.failures || []));
        if (data.bitacoraError) acc.bitacoraError = data.bitacoraError;
      }
      setResultado(acc);
    } catch (err) {
      // Se conserva lo acumulado: los lotes anteriores YA se ejecutaron.
      setResultado({ ...acc, interrumpido: true });
      setError(err.response?.data?.error || 'El lote falló. Lo anterior sí se ejecutó.');
    } finally {
      setCorriendo(false); setProgreso(null); setConfirmando(false);
      await cargarReporte();   // la tabla refleja lo que el cierre acaba de escribir
    }
  }

  // ── armado de la tabla: UNA fila por persona ──────────────────────────────
  const meses = [];
  if (data) for (let m = data.fromMonth; m <= data.untilMonth; m++) meses.push(m);

  const personas = [];
  if (data) {
    const idx = new Map();
    for (const f of data.filas) {
      if (!idx.has(f.persona_id)) {
        idx.set(f.persona_id, { id: f.persona_id, name: f.name, external_id: f.external_id,
                                activa: f.is_active, meses: {} });
        personas.push(idx.get(f.persona_id));
      }
      idx.get(f.persona_id).meses[f.month] = f;
    }
  }
  const q = busca.trim().toLowerCase();
  const visibles = q ? personas.filter(p => (p.name || '').toLowerCase().includes(q) ||
                                            String(p.external_id || '').toLowerCase().includes(q))
                     : personas;

  const rango = fromMonth === untilMonth ? MESES[fromMonth] : `${MESES[fromMonth]}–${MESES[untilMonth]}`;

  // ── Export ────────────────────────────────────────────────────────────────
  /**
   * ⚠ EL ESTADO DEL SELLO VA DENTRO DEL ARCHIVO, no solo en la pantalla.
   * En el portal nadie confunde números provisionales: el aviso está al lado de la
   * tabla. Un CSV adjunto a un correo pierde ese contexto entero, y un balance de PTO
   * sin sellar reenviado tres veces se vuelve "el número" para alguien. Va en la
   * primera línea y en el nombre del archivo.
   */
  const leyendaSello =
    estadoSello === 'todo'   ? 'PERÍODO SELLADO'
  : estadoSello === 'parcial' ? `PARCIALMENTE SELLADO — sellados: ${mesesSellados.map(m => MESES[m]).join(', ')}. El resto son números PROVISIONALES`
  :                             'PERÍODO NO SELLADO — números PROVISIONALES, todavía recalculables';

  const sufijoArchivo = estadoSello === 'todo' ? 'sellado' : 'provisional';
  const hoyLegible = new Date().toLocaleDateString('es-PR', { year: 'numeric', month: 'short', day: 'numeric' });

  /**
   * ⚠ FORMATO CONTABLE DE LOS NEGATIVOS: entre paréntesis, como el Excel de la contable.
   * Excel interpreta "(126.92)" como −126.92 al importar, así que sigue siendo sumable —
   * no es solo cosmética. Un saldo negativo es tiempo adelantado que el empleado debe y
   * REDUCE el pasivo neto, así que entra con su signo.
   */
  const dinero = (v) => {
    const abs = Math.abs(v).toFixed(2);
    return v < 0 ? `(${abs})` : abs;
  };

  function exportarTabla() {
    // ⚠⚠ DOS MARCADORES DISTINTOS, PORQUE SON DOS COSAS DISTINTAS. El backend devuelve
    // `sick_liability: null` tanto para "no tiene tarifa cargada" como para "está
    // inactiva y su enfermedad no es pasivo", y se desambigua con `is_active` (así lo
    // documenta getBalancesReport). Colapsar los dos en un mismo guion escondería cuál
    // de los dos huecos es — y uno es dato faltante mientras el otro es una regla.
    const SIN_TARIFA = '—';
    const NO_COMPUTA = 'no computa';

    const costoDe = (p) => {
      if (!costos) return [costosVeda ? 'sin permiso' : SIN_TARIFA, '', ''];
      const c = costos.porPersona.get(p.id);
      if (!c) return [SIN_TARIFA, SIN_TARIFA, SIN_TARIFA];
      const rate = c.hourly_rate === null ? SIN_TARIFA : Number(c.hourly_rate).toFixed(2);
      const vac  = c.vacation_liability === null ? SIN_TARIFA : dinero(c.vacation_liability);
      const enf  = c.sick_liability !== null ? dinero(c.sick_liability)
                 : c.hourly_rate === null    ? SIN_TARIFA
                 : NO_COMPUTA;                          // activa=false → la regla, no un hueco
      return [rate, vac, enf];
    };

    const filas = [
      [`Reporte PTO — ${year} ${rango} — generado ${hoyLegible}`],
      [leyendaSello],
      [`${visibles.length} de ${personas.length} personas` + (q ? ` (filtrado por «${busca.trim()}»)` : '') +
       ' · no incluye subcontratados (fuera del régimen)'],
      // ⚠ EL COSTO NO ES DEL RANGO, ES DEL SALDO ACTUAL DEL AÑO, y hay que decirlo o se
      // lee como si fuera el pasivo al cierre de `untilMonth`. Sale del saldo vigente
      // (el mismo que el dashboard), así que si agosto ya cerró, agosto está adentro.
      [`Análisis de Costo: sobre el saldo VIGENTE de ${year}, no sobre el cierre de ${MESES[untilMonth]}. ` +
       `Negativos entre paréntesis (reducen el pasivo). «${SIN_TARIFA}» = sin tarifa cargada; ` +
       `«${NO_COMPUTA}» = inactiva, su enfermedad no es pasivo.`],
      [],
      // Encabezado de DOS niveles, igual que en pantalla y que el Excel: el mes ocupa
      // la primera fila sobre sus 7 métricas.
      ['', '', ...meses.flatMap(m => [MESES[m], ...Array(METRICAS.length - 1).fill('')]),
       'Análisis de Costo', '', ''],
      ['ID', 'Nombre', ...meses.flatMap(() => METRICAS.map(x => x.label)),
       'Rate $/hr', '$Vac', '$Enf'],
      ...visibles.map(p => [
        p.external_id || p.id,
        p.name + (p.activa === false ? ' (inactiva)' : ''),
        // ⚠ El hueco sale VACÍO, no 0: un mes no cerrado no es un saldo en cero, y en
        // una hoja de dinero esa diferencia es la que hace que el contraste sirva.
        ...meses.flatMap(m => METRICAS.map(met => {
          const v = p.meses[m]?.[met.key];
          return v === null || v === undefined ? '' : n2(v);
        })),
        ...costoDe(p),
      ]),
    ];

    // ── Totales del pasivo ────────────────────────────────────────────────────
    // ⚠⚠ EN TRES PIEZAS, NO SOLO EL NETO. Un descubierto grande —hay un caso de −572 h de
    // enfermedad— baja el neto y alguien puede leer un pasivo MENOR del real. El bruto
    // adeudado y lo adelantado van en columnas propias para que el neto sea explicable.
    // ⚠ Son de TODA LA POBLACIÓN y no del filtro: salen del endpoint, que es la única
    // expresión de la regla. Sumarlos acá sobre las filas filtradas sería re-derivar los
    // buckets positivo/negativo, o sea la segunda expresión que esto vino a evitar.
    if (costos) {
      const t = costos.totals;
      filas.push([], ['TOTALES DEL PASIVO — toda la población, NO afectado por el filtro'],
        ['', 'Adeudado', 'Adelantado', 'Neto'],
        ['Vacaciones', dinero(t.vacation.positive), dinero(t.vacation.negative), dinero(t.vacation.net)],
        ['Enfermedad', dinero(t.sick.positive),     dinero(t.sick.negative),     dinero(t.sick.net)],
        ['Total neto', '', '', dinero(t.net_total)],
        [],
        [`${t.with_rate} personas con tarifa · ${t.without_rate} SIN tarifa cargada, ` +
         `cuyo costo NO está incluido en los totales`],
        [`${t.active} activas · ${t.inactive} inactivas (sus vacaciones sí computan; su enfermedad no)`]);
    } else {
      filas.push([], [costosVeda
        ? 'Análisis de Costo NO INCLUIDO: tu rol no tiene acceso al reporte de saldos.'
        : 'Análisis de Costo NO INCLUIDO: no se pudo leer el reporte de saldos.']);
    }

    descargarCSV(`pto_${year}_${MESES[fromMonth].toLowerCase()}-${MESES[untilMonth].toLowerCase()}_${sufijoArchivo}.csv`, filas);
  }

  /**
   * Los ajustes van en un archivo APARTE y no como una 8ª métrica por mes: serían 56
   * columnas, y el comentario —que es la mitad del valor de un ajuste— no entra en una
   * celda de una tabla que ya se lee de costado.
   */
  function exportarAjustes() {
    const filas = [
      [`Ajustes manuales de PTO — ${year} ${rango} — generado ${hoyLegible}`],
      [leyendaSello],
      [],
      ['ID', 'Nombre', 'Mes', 'Tipo', 'Horas', 'Motivo', 'Registrado por', 'Fecha'],
      ...ajustes.map(a => [
        a.external_id || a.persona_id, a.name, MESES[a.month],
        a.leave_type === 'vacation' ? 'Vacaciones' : 'Enfermedad',
        n2(a.hours), a.comment, a.adjusted_by_name || '—',
        new Date(a.created_at).toLocaleDateString('es-PR', { year: 'numeric', month: 'short', day: 'numeric' }),
      ]),
    ];
    descargarCSV(`pto_ajustes_${year}_${MESES[fromMonth].toLowerCase()}-${MESES[untilMonth].toLowerCase()}.csv`, filas);
  }

  // ── Sellado ───────────────────────────────────────────────────────────────
  async function revisarSello(month) {
    setError(null);
    try {
      const { data } = await client.get('/api/pto/sello/preview', { params: { year, month } });
      setSello(data);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo evaluar el sello.');
    }
  }

  async function confirmarSello(month) {
    try {
      await client.post('/api/pto/sello', { year, month });
      setSello(null);
      await recargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo sellar el mes.');
      setSello(null);
    }
  }

  async function reabrir(month, motivo) {
    try {
      await client.delete(`/api/pto/sello/${year}/${month}`, { data: { comment: motivo } });
      setReabriendo(null);
      await recargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo reabrir el mes.');
      setReabriendo(null);
    }
  }

  return (
    <div>
      {/* ── Período ───────────────────────────────────────────────────────── */}
      <div style={{ ...bubbleStyle, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={lbl}>Año</label>
            <input type="number" value={year} onChange={e => setYear(parseInt(e.target.value, 10) || year)}
                   style={{ ...fieldStyle, width: 88 }} />
          </div>
          <div>
            <label style={lbl}>Desde</label>
            <select value={fromMonth} onChange={e => setFromMonth(+e.target.value)} style={{ ...fieldStyle, width: 104 }}>
              {MESES.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Hasta</label>
            <select value={untilMonth} onChange={e => setUntilMonth(+e.target.value)} style={{ ...fieldStyle, width: 104 }}>
              {MESES.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <button onClick={cargarReporte} disabled={cargando || !rangoOk}
                  style={{ ...navyBtn, padding: '8px 16px', fontSize: 12, opacity: (cargando || !rangoOk) ? 0.5 : 1 }}>
            {cargando ? 'Generando…' : 'Ver reporte'}
          </button>
          {data && (
            <div style={{ position: 'relative', marginLeft: 'auto' }}>
              <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar nombre o ID"
                     style={{ ...fieldStyle, paddingLeft: 28, width: 220 }} />
            </div>
          )}
        </div>
        {!rangoOk && <div style={{ ...aviso, marginTop: 10 }}><AlertTriangle size={13} /> «Hasta» no puede ser anterior a «Desde».</div>}
      </div>

      {error && <div style={{ ...bubbleStyle, ...errBox, marginBottom: 14 }}>{error}</div>}

      {/* ── Franja de sellos (V64) ─────────────────────────────────────────
          Visible para TODO el módulo, no solo para admin: saber si los números
          están sellados es parte de leer la tabla, no una acción. Los botones sí
          son de admin — misma lógica que el bloque de cierre. */}
      {data && (
        <FranjaSellos
          meses={meses} sellados={sellados} estado={estadoSello} year={year}
          puedeSellar={puedeOperar}
          onRevisar={revisarSello} onReabrir={(m) => setReabriendo(m)}
          ultimoSellado={sellados.length ? sellados[sellados.length - 1] : null}
        />
      )}

      {/*
        BLOQUE DE CIERRE — admin + contabilidad. Una de las tres cosas que ESCRIBEN.

        ⚠ V64 CAMBIÓ ESTE GATE. Hasta V57 era solo-admin, con este razonamiento escrito
        acá: la contable es la AUDIENCIA de la tabla, el backend le devolvía 403, y
        mostrarle el botón habría sido un callejón sin salida en vez de una restricción
        legible. El razonamiento seguía siendo correcto — lo que cambió es el permiso
        del backend, y las dos puntas se movieron en el mismo commit, que es justamente
        lo que ese comentario pedía.

        El "si algún día la contable debe poder cerrar" que decía la nota vieja llegó en
        V64, junto con ajustar y sellar: los tres se abrieron a la vez porque darle dos
        de tres la dejaba trabada (ver ROLES_CIERRE en ptoCloseController).

        ⚠ La TABLA sigue visible para todo el módulo, que es lo correcto: leer el reporte
        es justamente para lo que se entra acá. El gate es sobre la acción, no sobre el
        contenido — el revisor, que no opera, ve la tabla y no los botones.
      */}
      {puedeOperar && (
        <BloqueCierre
          rango={rango} rangoOk={rangoOk} corriendo={corriendo} progreso={progreso}
          resultado={resultado} onPreview={cargarPreview} onReintentar={correr}
        />
      )}

      {/* ── Tabla ─────────────────────────────────────────────────────────── */}
      {data && (
        <div style={bubbleStyle}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
              {visibles.length} de {personas.length} personas · {meses.length} meses
            </span>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>
              No incluye a los subcontratados (fuera del régimen).
            </span>
            <button onClick={exportarTabla} style={{ ...botonSecundario, marginLeft: 'auto' }}>
              <Download size={12} /> Exportar tabla
            </button>
          </div>

          {/* La tabla es MUY ancha por diseño (2 + 7×N columnas), igual que el Excel.
              El scroll horizontal es del contenedor, nunca del body, y las columnas de
              identidad quedan fijas — que es lo que hace posible el contraste. */}
          <div style={{ overflow: 'auto', maxHeight: '68vh', border: `1px solid ${BORDER_COLOR}`, borderRadius: 8 }}>
            <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 11 }}>
              <thead>
                <tr>
                  <th style={{ ...th, ...thFijo, left: 0, top: 0, zIndex: 6, minWidth: W_ID, textAlign: 'left' }} rowSpan={2}>ID</th>
                  <th style={{ ...th, ...thFijo, left: W_ID, top: 0, zIndex: 6, minWidth: W_NOM, textAlign: 'left',
                               borderRight: `2px solid rgba(255,255,255,.35)` }} rowSpan={2}>Nombre</th>
                  {meses.map((m, i) => (
                    <th key={m} colSpan={METRICAS.length}
                        style={{ ...th, top: 0, zIndex: 4, height: H_FILA1,
                                 borderLeft: i === 0 ? 'none' : `2px solid rgba(255,255,255,.35)` }}>
                      {MESES[m]}
                    </th>
                  ))}
                </tr>
                <tr>
                  {meses.map((m, i) => METRICAS.map((met, j) => (
                    <th key={`${m}-${met.key}`}
                        style={{ ...th, top: H_FILA1, zIndex: 4, minWidth: 46, fontSize: 10,
                                 fontWeight: met.fuerte ? 800 : 600,
                                 borderLeft: (i > 0 && j === 0) ? `2px solid rgba(255,255,255,.35)` : 'none' }}>
                      {met.label}
                    </th>
                  )))}
                </tr>
              </thead>
              <tbody>
                {visibles.map((p, r) => {
                  const bg = r % 2 ? '#fafbfc' : '#fff';
                  return (
                    <tr key={p.id}>
                      <td style={{ ...td, ...tdFijo, left: 0, zIndex: 2, background: bg,
                                   fontFamily: 'monospace', color: '#64748b' }}>
                        {p.external_id || p.id}
                      </td>
                      <td style={{ ...td, ...tdFijo, left: W_ID, zIndex: 2, background: bg,
                                   fontWeight: 600, color: NAVY, borderRight: `2px solid ${BORDER_COLOR}` }}>
                        {p.name}
                        {p.activa === false && <span style={{ fontSize: 9, color: '#92400e' }}> · inactiva</span>}
                      </td>
                      {meses.map((m, i) => METRICAS.map((met, j) => {
                        const f = p.meses[m];
                        const num = n2(f ? f[met.key] : undefined);
                        return (
                          <td key={`${m}-${met.key}`}
                              style={{ ...td, background: bg, textAlign: 'right',
                                       fontWeight: met.fuerte ? 700 : 400,
                                       color: num === null ? '#cbd5e1' : (met.fuerte ? NAVY : '#334155'),
                                       borderLeft: (i > 0 && j === 0) ? `2px solid ${BORDER_COLOR}` : 'none' }}>
                            {/* ⚠ null = MES NO CERRADO, no saldo cero. No se colapsa a 0:
                                en una tabla de dinero un 0 es indistinguible de un saldo
                                genuinamente en cero y la contable no vería el hueco. */}
                            {num === null ? '—' : num}
                          </td>
                        );
                      }))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, lineHeight: 1.5 }}>
            <strong>—</strong> en BAL significa <strong>mes no cerrado</strong>, no saldo cero.
            Si aparece donde el Excel muestra un número, ese mes todavía no se cerró.
            {' '}Columnas por mes: <strong>hrs</strong> trabajadas · <strong>vAc/vPd/BALv</strong> vacaciones
            (acumuladas, pagadas, balance) · <strong>eAc/ePd/BALe</strong> enfermedad.
            {/* ⚠ El export trae TRES columnas que esta tabla no muestra. Decirlo acá es lo
                mínimo honesto: un botón "Exportar tabla" que produce columnas que la tabla
                no tiene induce a pensar que falta algo en el archivo, o peor, a no buscarlas. */}
            <br />
            El <strong>archivo exportado</strong> agrega el bloque de <strong>Análisis de Costo</strong>
            {' '}(Rate $/hr, $Vac, $Enf) y los totales del pasivo, que esta tabla no muestra.
            {costosVeda && <> <strong style={{ color: '#92400e' }}>Tu rol no accede a esos montos,
              así que el archivo va a salir sin ellos.</strong></>}
          </div>
        </div>
      )}

      {/* ── Ajustes del período (V64) ────────────────────────────────────── */}
      {data && (
        <BloqueAjustes
          ajustes={ajustes} rango={rango} puedeAjustar={puedeOperar}
          hayAlgo={personas.length > 0}
          onNuevo={() => setAjustando({})} onExportar={exportarAjustes}
        />
      )}

      {confirmando && preview && (
        <ModalConfirmar preview={preview} rango={rango} corriendo={corriendo}
                        lotes={Math.ceil(preview.aCerrar / TAM_LOTE)}
                        onCancel={() => setConfirmando(false)}
                        onConfirm={() => correr(preview.personaIds)} />
      )}

      {sello && (
        <ModalSello ev={sello} year={year}
                    onCancel={() => setSello(null)}
                    onConfirm={() => confirmarSello(sello.month)} />
      )}

      {reabriendo !== null && (
        <ModalReabrir month={reabriendo} year={year}
                      onCancel={() => setReabriendo(null)}
                      onConfirm={(motivo) => reabrir(reabriendo, motivo)} />
      )}

      {ajustando && (
        <ModalAjuste personas={personas} meses={meses} sellados={sellados} year={year}
                     onCancel={() => setAjustando(null)}
                     onHecho={async () => { setAjustando(null); await recargar(); }} />
      )}
    </div>
  );
}

/**
 * La franja de estado del sello. Es lo que dice si los números de la tabla son
 * definitivos, así que va ARRIBA de la tabla y no escondida en un menú.
 */
function FranjaSellos({ meses, sellados, estado, year, puedeSellar, onRevisar, onReabrir, ultimoSellado }) {
  // El próximo sellable es el primer mes ABIERTO del rango: el sellado va en orden, así
  // que ofrecer cualquier otro sería ofrecer un 409.
  const proximo = meses.find(m => !sellados.includes(m)) ?? null;
  const t = estado === 'todo' ? TONOS.ok : estado === 'parcial' ? TONOS.warn : TONOS.mute;
  return (
    <div style={{ ...bubbleStyle, marginBottom: 14, borderLeft: `3px solid ${t.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {meses.map(m => {
            const cerrado = sellados.includes(m);
            return (
              <span key={m} title={cerrado ? 'Sellado — no se recalcula' : 'Abierto — todavía recalculable'}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700,
                             padding: '3px 9px', borderRadius: 20,
                             background: cerrado ? TONOS.ok.bg : '#f8fafc',
                             color: cerrado ? TONOS.ok.color : '#64748b',
                             border: `1px solid ${cerrado ? TONOS.ok.border : BORDER_COLOR}` }}>
                {cerrado ? <Lock size={11} /> : <Unlock size={11} />} {MESES[m]}
              </span>
            );
          })}
        </div>
        <span style={{ fontSize: 11, color: '#64748b', lineHeight: 1.45, flex: 1, minWidth: 240 }}>
          {estado === 'todo'
            ? 'Todo el período está sellado: estos números son definitivos.'
            : estado === 'parcial'
              ? 'Parte del período sigue abierto. Los meses abiertos pueden cambiar si se recalcula.'
              : 'Ningún mes del período está sellado: los números son provisionales.'}
        </span>
        {puedeSellar && proximo !== null && (
          <button onClick={() => onRevisar(proximo)} style={{ ...navyBtn, padding: '7px 14px', fontSize: 12,
                    display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Lock size={13} /> Sellar {MESES[proximo]}
          </button>
        )}
        {puedeSellar && ultimoSellado !== null && (
          <button onClick={() => onReabrir(ultimoSellado)} style={botonSecundario}>
            <Unlock size={12} /> Reabrir {MESES[ultimoSellado]}
          </button>
        )}
      </div>
      {/* ⚠ La dirección del sello es contraintuitiva y la gente la asume al revés. */}
      {sellados.length > 0 && (
        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 8, lineHeight: 1.5 }}>
          Un sello congela ese mes <strong>y todos los anteriores</strong>. Los meses posteriores
          siguen cerrando normalmente. Solo se puede reabrir el último sellado.
        </div>
      )}
    </div>
  );
}

/** Los asientos del período. Read-only para todos; crear es de admin. */
function BloqueAjustes({ ajustes, rango, puedeAjustar, hayAlgo, onNuevo, onExportar }) {
  return (
    <div style={{ ...bubbleStyle, marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
          Ajustes manuales de {rango} · {ajustes.length}
        </span>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>
          Correcciones al saldo que el cálculo no produce solo.
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {ajustes.length > 0 && (
            <button onClick={onExportar} style={botonSecundario}><Download size={12} /> Exportar</button>
          )}
          {puedeAjustar && hayAlgo && (
            <button onClick={onNuevo} style={{ ...navyBtn, padding: '7px 14px', fontSize: 12,
                      display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Plus size={13} /> Nuevo ajuste
            </button>
          )}
        </div>
      </div>

      {ajustes.length === 0 ? (
        <div style={{ fontSize: 12, color: '#94a3b8', padding: '10px 0' }}>
          Ningún ajuste en este período. El saldo sale entero del cálculo.
        </div>
      ) : (
        <div style={{ border: `1px solid ${BORDER_COLOR}`, borderRadius: 8, overflow: 'hidden' }}>
          {ajustes.map(a => (
            <div key={a.id} style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap',
                                     padding: '7px 11px', borderBottom: `1px solid ${BORDER_COLOR}`, fontSize: 11 }}>
              <span style={{ fontFamily: 'monospace', color: '#64748b', minWidth: 62 }}>{a.external_id || a.persona_id}</span>
              <strong style={{ color: NAVY, minWidth: 150 }}>{a.name}</strong>
              <span style={{ color: '#64748b', minWidth: 34 }}>{MESES[a.month]}</span>
              <span style={{ color: '#64748b', minWidth: 74 }}>
                {a.leave_type === 'vacation' ? 'Vacaciones' : 'Enfermedad'}
              </span>
              {/* El signo va explícito: un ajuste es un movimiento, no un valor. */}
              <span style={{ fontWeight: 800, minWidth: 56, textAlign: 'right',
                             color: a.hours >= 0 ? '#166534' : '#991b1b' }}>
                {a.hours >= 0 ? '+' : ''}{n2(a.hours)} h
              </span>
              <span style={{ color: '#334155', flex: 1, minWidth: 180 }}>{a.comment}</span>
              <span style={{ color: '#94a3b8', fontSize: 10 }}>{a.adjusted_by_name || '—'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** El sello: lo que lo bloquea, con nombres, o la confirmación. */
function ModalSello({ ev, year, onCancel, onConfirm }) {
  return (
    <div style={overlay}>
      <div style={caja}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: NAVY, margin: '0 0 12px' }}>
          Sellar {MESES[ev.month]} {year}
        </h2>

        {ev.puedeSellar ? (
          <>
            <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.6, marginBottom: 12 }}>
              El mes está cerrado para toda la población y los meses anteriores ya están sellados.
            </div>
            <div style={{ ...aviso, marginBottom: 12 }}>
              <AlertTriangle size={13} />
              <span>
                Sellar congela <strong>{MESES[ev.month]} y todo lo anterior</strong>: no se van a poder
                recalcular ni ajustar. Se puede reabrir, pero solo desde el último sellado hacia atrás.
              </span>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.6, marginBottom: 12 }}>
              Todavía no se puede sellar. Sellar con huecos congelaría un balance que nunca se calculó.
            </div>
            {ev.previosSinSellar?.length > 0 && (
              <div style={{ ...aviso, marginBottom: 8 }}>
                <AlertTriangle size={13} />
                <span>
                  El sellado va <strong>en orden</strong>: falta sellar{' '}
                  <strong>{ev.previosSinSellar.map(m => MESES[m]).join(', ')}</strong>.
                </span>
              </div>
            )}
            {ev.sinClasificar?.length > 0 && (
              <Desplegable titulo={`${ev.sinClasificar.length} sin clasificar — hay que clasificarlas primero`}
                nota="El cierre las rehúsa mientras no tengan régimen, así que nunca van a tener fila de este mes. Se define en Empleados."
                items={ev.sinClasificar} tono="warn" />
            )}
            {ev.faltantes?.length > 0 && (
              <Desplegable titulo={`${ev.faltantes.length} sin el mes cerrado`}
                nota="Están clasificadas pero les falta correr el cierre de este mes. Alcanza con cerrar el período de nuevo: es idempotente."
                items={ev.faltantes} tono="warn" />
            )}
          </>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button onClick={onCancel} style={botonCancelar}>Cerrar</button>
          {ev.puedeSellar && (
            <button onClick={onConfirm} style={{ ...navyBtn, padding: '7px 18px', fontSize: 12 }}>
              Sellar {MESES[ev.month]}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Reabrir. El motivo es OBLIGATORIO por la misma razón que en el ajuste: el DELETE se
 * lleva `closed_by` y `closed_at`, así que el asiento de audit_log es la única
 * evidencia que queda de que este mes estuvo sellado y alguien lo abrió.
 */
function ModalReabrir({ month, year, onCancel, onConfirm }) {
  const [motivo, setMotivo] = useState('');
  const listo = motivo.trim().length >= 3;
  return (
    <div style={overlay}>
      <div style={{ ...caja, maxWidth: 460 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: NAVY, margin: '0 0 12px' }}>
          Reabrir {MESES[month]} {year}
        </h2>
        <div style={{ ...aviso, marginBottom: 12 }}>
          <AlertTriangle size={13} />
          <span>
            El mes vuelve a ser <strong>recalculable y ajustable</strong>. Los números
            exportados como definitivos pueden dejar de coincidir.
          </span>
        </div>
        <label style={lbl}>Motivo (obligatorio)</label>
        <input value={motivo} onChange={e => setMotivo(e.target.value)} autoFocus
               style={{ ...fieldStyle, width: '100%', marginBottom: 14 }}
               placeholder="Por qué hay que reabrir un mes ya conciliado" />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={botonCancelar}>Cancelar</button>
          <button onClick={() => onConfirm(motivo.trim())} disabled={!listo}
                  style={{ ...navyBtn, padding: '7px 18px', fontSize: 12, opacity: listo ? 1 : 0.5 }}>
            Reabrir {MESES[month]}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Nuevo ajuste.
 *
 * ⚠ LA CONTABLE PIENSA EN EL OBJETIVO ("dice 32 y debería decir 40"), NO EN EL DELTA.
 * Así que el formulario pide el objetivo y MUESTRA el delta derivado antes de
 * confirmar — pero lo que se manda y se persiste es el delta. Con absolutos, dos
 * ajustes sobre la misma persona se pisan y el log deja de ser sumable.
 */
function ModalAjuste({ personas, meses, sellados, year, onCancel, onHecho }) {
  const abiertos = meses.filter(m => !sellados.includes(m));
  const [personaId, setPersonaId] = useState('');
  const [month, setMonth] = useState(abiertos[abiertos.length - 1] ?? meses[meses.length - 1]);
  const [leaveType, setLeaveType] = useState('vacation');
  const [objetivo, setObjetivo] = useState('');
  const [comment, setComment] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState(null);

  const persona = personas.find(p => String(p.id) === String(personaId));
  const fila = persona?.meses?.[month];
  const actual = fila ? fila[leaveType === 'vacation' ? 'bal_vac' : 'bal_enf'] : null;
  const cerrado = actual !== null && actual !== undefined;
  const objetivoNum = objetivo === '' ? null : Number(objetivo);
  const delta = (cerrado && objetivoNum !== null && Number.isFinite(objetivoNum))
    ? Math.round((objetivoNum - actual) * 100) / 100 : null;
  const listo = persona && cerrado && delta !== null && delta !== 0 && comment.trim().length >= 3;

  async function guardar() {
    setGuardando(true); setErr(null);
    try {
      await client.post('/api/pto/ajustes', {
        personaId: persona.id, year, month, leaveType, hours: delta, comment: comment.trim(),
      });
      await onHecho();
    } catch (e) {
      setErr(e.response?.data?.error || 'No se pudo registrar el ajuste.');
      setGuardando(false);
    }
  }

  return (
    <div style={overlay}>
      <div style={caja}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: NAVY, margin: '0 0 12px' }}>Nuevo ajuste</h2>

        <div style={{ display: 'grid', gap: 10, marginBottom: 12 }}>
          <div>
            <label style={lbl}>Persona</label>
            <select value={personaId} onChange={e => setPersonaId(e.target.value)} style={{ ...fieldStyle, width: '100%' }}>
              <option value="">Elegir…</option>
              {personas.map(p => (
                <option key={p.id} value={p.id}>{p.external_id || p.id} · {p.name}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Mes</label>
              <select value={month} onChange={e => setMonth(+e.target.value)} style={{ ...fieldStyle, width: '100%' }}>
                {meses.map(m => (
                  <option key={m} value={m} disabled={sellados.includes(m)}>
                    {MESES[m]}{sellados.includes(m) ? ' — sellado' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Tipo</label>
              <select value={leaveType} onChange={e => setLeaveType(e.target.value)} style={{ ...fieldStyle, width: '100%' }}>
                <option value="vacation">Vacaciones</option>
                <option value="sick">Enfermedad</option>
              </select>
            </div>
          </div>

          {persona && !cerrado && (
            <div style={aviso}>
              <AlertTriangle size={13} />
              <span>
                {persona.name} no tiene <strong>{MESES[month]}</strong> cerrado. Hay que cerrar el mes
                antes de ajustarlo: si no, el ajuste no aparecería en la tabla.
              </span>
            </div>
          )}

          {persona && cerrado && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Balance actual</label>
                <div style={{ ...fieldStyle, background: '#f8fafc', color: '#64748b', fontWeight: 700 }}>{n2(actual)} h</div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Debería decir</label>
                <input type="number" step="0.01" value={objetivo} onChange={e => setObjetivo(e.target.value)}
                       style={{ ...fieldStyle, width: '100%' }} placeholder="0.00" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Ajuste</label>
                <div style={{ ...fieldStyle, background: '#f8fafc', fontWeight: 800,
                              color: delta === null || delta === 0 ? '#94a3b8' : (delta > 0 ? '#166534' : '#991b1b') }}>
                  {delta === null ? '—' : `${delta > 0 ? '+' : ''}${n2(delta)} h`}
                </div>
              </div>
            </div>
          )}

          <div>
            <label style={lbl}>Motivo (obligatorio)</label>
            <input value={comment} onChange={e => setComment(e.target.value)} style={{ ...fieldStyle, width: '100%' }}
                   placeholder="Por qué el saldo calculado no es el correcto" />
          </div>
        </div>

        <div style={{ ...aviso, marginBottom: 12 }}>
          <AlertTriangle size={13} />
          <span>
            El ajuste queda como <strong>asiento permanente</strong>: no se edita ni se borra. Si estuvo
            mal, se corrige con otro ajuste. Al guardarlo se recalcula el rastro desde {MESES[month]}.
          </span>
        </div>

        {err && <div style={{ ...errBox, padding: '8px 11px', borderRadius: 6, marginBottom: 10 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} disabled={guardando} style={botonCancelar}>Cancelar</button>
          <button onClick={guardar} disabled={!listo || guardando}
                  style={{ ...navyBtn, padding: '7px 18px', fontSize: 12, opacity: (!listo || guardando) ? 0.5 : 1 }}>
            {guardando ? 'Guardando…' : 'Registrar ajuste'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** El bloque que ESCRIBE. Separado para poder ocultarlo por rol sin tocar la tabla. */
function BloqueCierre({ rango, rangoOk, corriendo, progreso, resultado, onPreview, onReintentar }) {
  return (
    <div style={{ ...bubbleStyle, marginBottom: 14, borderLeft: `3px solid ${NAVY}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button onClick={onPreview} disabled={corriendo || !rangoOk}
                style={{ ...navyBtn, padding: '8px 16px', fontSize: 12,
                         display: 'inline-flex', alignItems: 'center', gap: 7,
                         opacity: (corriendo || !rangoOk) ? 0.5 : 1 }}>
          <Play size={14} /> Cerrar {rango}
        </button>
        <span style={{ fontSize: 11, color: '#64748b', lineHeight: 1.45, flex: 1, minWidth: 260 }}>
          Calcula acumulación, topes y balances del período. <strong>No sella</strong>:
          el período queda recalculable hasta validarlo contra el Excel.
        </span>
      </div>

      {progreso && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, color: NAVY, fontWeight: 600, marginBottom: 6 }}>
            Lote {progreso.lote} de {progreso.lotes} · {progreso.personas} / {progreso.total} personas
          </div>
          <div style={{ height: 7, background: '#e2e8f0', borderRadius: 20, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(progreso.personas / progreso.total) * 100}%`,
                          background: NAVY, transition: 'width .3s' }} />
          </div>
        </div>
      )}

      {resultado && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${BORDER_COLOR}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 10,
                        display: 'flex', alignItems: 'center', gap: 7 }}>
            {resultado.interrumpido
              ? <><AlertTriangle size={15} color="#b45309" /> Corrida interrumpida</>
              : <><CheckCircle2 size={15} color="#16a34a" /> Corrida completa</>}
          </div>
          {/* Las CUATRO categorías separadas: un reintento que no cerró nada nuevo tiene
              que verse distinto de una corrida limpia. */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <Tarjeta n={resultado.closed}  label="Cerrados ahora" tono="ok"   sub="meses" />
            <Tarjeta n={resultado.already} label="Ya estaban"     tono="mute" sub="meses" />
            <Tarjeta n={resultado.refused} label="Rehusados"      tono="warn" sub="meses" />
            <Tarjeta n={resultado.failed}  label="Fallaron"       tono={resultado.failed ? 'err' : 'mute'} sub="personas" />
          </div>
          {Object.keys(resultado.refusedByReason).length > 0 && (
            <div style={{ marginBottom: 10 }}>
              {Object.entries(resultado.refusedByReason).map(([k, v]) => (
                <div key={k} style={{ fontSize: 11, color: '#475569' }}>
                  <strong>{v}</strong> — {RAZONES[k] || k}
                </div>
              ))}
            </div>
          )}
          {resultado.failures.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#991b1b', marginBottom: 4 }}>
                Fallos — con nombre para poder reintentar
              </div>
              <div style={{ maxHeight: 160, overflowY: 'auto', border: `1px solid ${BORDER_COLOR}`, borderRadius: 6 }}>
                {resultado.failures.map(f => (
                  <div key={f.persona_id} style={{ fontSize: 11, padding: '4px 8px', borderBottom: `1px solid ${BORDER_COLOR}` }}>
                    <span style={{ fontFamily: 'monospace', color: '#64748b' }}>{f.external_id || f.persona_id}</span>
                    {' · '}<strong>{f.name || '(sin nombre)'}</strong>
                    <div style={{ color: '#991b1b', fontSize: 10 }}>{f.error}</div>
                  </div>
                ))}
              </div>
              <button onClick={() => onReintentar(resultado.failures.map(f => f.persona_id))} disabled={corriendo}
                      style={{ ...navyBtn, padding: '6px 13px', fontSize: 11, marginTop: 8,
                               display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <RefreshCw size={12} /> Reintentar los {resultado.failures.length} fallidos
              </button>
            </div>
          )}
          {resultado.bitacoraError && (
            <div style={{ ...aviso, marginTop: 8 }}><AlertTriangle size={13} /> {resultado.bitacoraError}</div>
          )}
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 8 }}>
            Corrida <span style={{ fontFamily: 'monospace' }}>{resultado.runId}</span> · {resultado.lotes} lote(s) · queda en la bitácora.
          </div>
        </div>
      )}
    </div>
  );
}

function ModalConfirmar({ preview, rango, corriendo, lotes, onCancel, onConfirm }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 22, maxWidth: 520, width: '100%',
                    maxHeight: '86vh', overflowY: 'auto' }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: NAVY, margin: '0 0 12px' }}>Confirmar el cierre</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <Tarjeta n={preview.aCerrar} label="Se van a cerrar" tono="ok" />
          <Tarjeta n={preview.sinClasificar.length} label="Sin clasificar" tono="warn" />
          <Tarjeta n={preview.fueraDelRegimen.length} label="Fuera del régimen" tono="mute" />
        </div>
        <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.6, marginBottom: 12 }}>
          <strong>{preview.aCerrar} personas</strong> para <strong>{rango}</strong>, en{' '}
          <strong>{lotes} lote(s)</strong> de hasta {TAM_LOTE}.
        </div>
        {preview.sinClasificar.length > 0 && (
          <Desplegable titulo={`${preview.sinClasificar.length} sin clasificar — no se cierran`}
            nota="Falta definir su régimen en Empleados. No se cierran a propósito: cerrarlas como «No Exento» y reclasificarlas después dejaría acumulación que el motor no limpia sola."
            items={preview.sinClasificar} tono="warn" />
        )}
        {preview.fueraDelRegimen.length > 0 && (
          <Desplegable titulo={`${preview.fueraDelRegimen.length} fuera del régimen — no acumulan`}
            nota="Subcontratados: están en Nómina por factura y no participan del régimen. Es lo esperado."
            items={preview.fueraDelRegimen} tono="mute" />
        )}
        <div style={{ ...aviso, margin: '12px 0' }}>
          <AlertTriangle size={13} />
          <span>El cálculo es <strong>idempotente</strong>: si ya estaba cerrado no se duplica nada. Esto <strong>no sella</strong> el período.</span>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} disabled={corriendo}
                  style={{ background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 20,
                           padding: '7px 16px', fontSize: 12, color: '#64748b', cursor: 'pointer', fontFamily: 'inherit' }}>
            Cancelar
          </button>
          <button onClick={onConfirm} disabled={corriendo}
                  style={{ ...navyBtn, padding: '7px 18px', fontSize: 12, opacity: corriendo ? 0.6 : 1 }}>
            {corriendo ? 'Cerrando…' : 'Confirmar y cerrar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── piezas ──────────────────────────────────────────────────────────────────
const lbl = { fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3, display: 'block' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const caja = { background: '#fff', borderRadius: 12, padding: 22, maxWidth: 560, width: '100%',
               maxHeight: '86vh', overflowY: 'auto' };
const botonSecundario = { background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 20,
                          padding: '6px 13px', fontSize: 11, fontWeight: 600, color: '#475569',
                          cursor: 'pointer', fontFamily: 'inherit',
                          display: 'inline-flex', alignItems: 'center', gap: 5 };
const botonCancelar = { background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 20,
                        padding: '7px 16px', fontSize: 12, color: '#64748b',
                        cursor: 'pointer', fontFamily: 'inherit' };
const aviso = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '7px 10px',
                background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', borderRadius: 6 };
const errBox = { background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', fontSize: 13 };
const th = { position: 'sticky', background: NAVY, color: '#fff', fontWeight: 700,
             padding: '5px 7px', textAlign: 'center', whiteSpace: 'nowrap' };
const thFijo = { zIndex: 6 };
const td = { padding: '4px 7px', borderBottom: `1px solid ${BORDER_COLOR}`, whiteSpace: 'nowrap' };
const tdFijo = { position: 'sticky' };

const TONOS = {
  ok:   { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  warn: { bg: '#fffbeb', color: '#92400e', border: '#fde68a' },
  mute: { bg: '#f8fafc', color: '#475569', border: BORDER_COLOR },
  err:  { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
};

function Tarjeta({ n, label, tono, sub }) {
  const t = TONOS[tono] || TONOS.mute;
  return (
    <div style={{ background: t.bg, border: `1px solid ${t.border}`, borderRadius: 8, padding: '8px 12px', minWidth: 106 }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: t.color, lineHeight: 1.1 }}>{n}</div>
      <div style={{ fontSize: 10, color: t.color, fontWeight: 600 }}>{label}</div>
      {sub && <div style={{ fontSize: 9, color: '#94a3b8' }}>{sub}</div>}
    </div>
  );
}

function Desplegable({ titulo, nota, items, tono }) {
  const [abierto, setAbierto] = useState(false);
  const t = TONOS[tono] || TONOS.mute;
  return (
    <div style={{ border: `1px solid ${t.border}`, background: t.bg, borderRadius: 8, padding: '8px 11px', marginBottom: 8 }}>
      <button onClick={() => setAbierto(a => !a)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                       fontSize: 11, fontWeight: 700, color: t.color, fontFamily: 'inherit' }}>
        {abierto ? '▾' : '▸'} {titulo}
      </button>
      <div style={{ fontSize: 10, color: t.color, opacity: .85, marginTop: 3, lineHeight: 1.45 }}>{nota}</div>
      {abierto && (
        <div style={{ maxHeight: 180, overflowY: 'auto', marginTop: 7, background: '#fff',
                      border: `1px solid ${t.border}`, borderRadius: 6 }}>
          {items.map(p => (
            <div key={p.id} style={{ fontSize: 11, padding: '3px 7px', borderBottom: `1px solid ${BORDER_COLOR}` }}>
              <span style={{ fontFamily: 'monospace', color: '#64748b' }}>{p.external_id || p.id}</span> · {p.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
